import { type ReferenceImageItem,refreshReferenceImageUrlByObjectKey } from '../api/referenceImages'

/**
 * 图库图片「补签(new signed URL)」统一调度。
 *
 * 背景：七牛私有桶的下载链接带 `e`(过期时间) 与 `token`(签名)，一旦临期或被拒就必然加载失败。
 * 前端不可能自己算签名，只能向后端要一个新的（后端即时重签，本地 HMAC，成本≈0）。
 *
 * 但如果每张裂图都无脑发一次请求，图库整屏失败就会瞬间打出几十个并发请求
 * （列表 + 选择器 + 灯箱会同时失败），因此需要这一层统一约束：
 * - 同 key 并发合并：同一 objectKey 同时只会有 1 个在途请求，其余复用同一个 Promise
 * - 成功结果短时间复用：避免同一屏多处重复请求同一张图
 * - 失败冷却：失败后一小段时间内不再重试，避免刷新—失败—刷新打转
 * - 全局并发上限：整屏失败时排队执行，把请求摊开，不冲垮后端
 *
 * 调用方（列表卡片 / 参考图选择器 / 灯箱）只需传 executor；上层 UI 不必各自再去重。
 */
export type RefreshUrlExecutor = (objectKey: string) => Promise<ReferenceImageItem | null>

/** 同时最多几个补签请求在飞 */
const MAX_CONCURRENT = 4
/** 成功结果的复用窗口：这段时间内同 key 直接复用，不再请求 */
const SUCCESS_REUSE_MS = 30_000
/** 失败冷却窗口：这段时间内同 key 不再重试 */
const FAILURE_COOLDOWN_MS = 20_000

type CacheEntry = {
  at: number
  value: ReferenceImageItem | null
}

const inflight = new Map<string, Promise<ReferenceImageItem | null>>()
const cache = new Map<string, CacheEntry>()
let running = 0
const waiting: Array<() => void> = []

function acquireSlot(): Promise<void> {
  if (running < MAX_CONCURRENT) {
    running += 1
    return Promise.resolve()
  }
  return new Promise<void>((resolve) => {
    waiting.push(() => {
      running += 1
      resolve()
    })
  })
}

function releaseSlot(): void {
  running = Math.max(0, running - 1)
  const next = waiting.shift()
  if (next) next()
}

function readCache(key: string): { hit: boolean; value: ReferenceImageItem | null } {
  const entry = cache.get(key)
  if (!entry) return { hit: false, value: null }
  const ttl = entry.value ? SUCCESS_REUSE_MS : FAILURE_COOLDOWN_MS
  if (Date.now() - entry.at >= ttl) return { hit: false, value: null }
  return { hit: true, value: entry.value }
}

/**
 * 取一份新鲜签名。
 *
 * @param objectKey 后端对象键
 * @param executor 真正发起 refresh-url 的函数（便于调用方聚合同一无谓请求/回写列表）
 * @param opts.force 忽略成功/失败缓存强制重试（用于「失败自愈」与用户手动重试）
 * @returns 新条目；失败（或无 objectKey）时返回 null，调用方据此展示错误
 */
export function refreshReferenceUrlOnce(
  objectKey: string | undefined | null,
  executor: RefreshUrlExecutor,
  opts?: { force?: boolean },
): Promise<ReferenceImageItem | null> {
  const key = (objectKey ?? '').trim()
  if (!key) return Promise.resolve(null)

  if (!opts?.force) {
    const cached = readCache(key)
    if (cached.hit) return Promise.resolve(cached.value)
  }

  const pending = inflight.get(key)
  if (pending) return pending

  const task = (async (): Promise<ReferenceImageItem | null> => {
    await acquireSlot()
    try {
      const value = await executor(key)
      cache.set(key, { at: Date.now(), value })
      return value
    } catch {
      cache.set(key, { at: Date.now(), value: null })
      return null
    } finally {
      inflight.delete(key)
      releaseSlot()
    }
  })()

  inflight.set(key, task)
  return task
}

/** 默认 executor：直接调后端 refresh-url 并做 405→PUT 降级 */
export const defaultRefreshUrlExecutor: RefreshUrlExecutor = async (objectKey) => {
  try {
    return await refreshReferenceImageUrlByObjectKey(objectKey)
  } catch {
    return null
  }
}

/** 清空缓存（如用户更换账号/存储配置后） */
export function clearReferenceUrlRefreshCache(objectKey?: string): void {
  if (objectKey) {
    cache.delete(objectKey.trim())
    return
  }
  cache.clear()
}
